<template>
  <transition name="la-legend-fade">
    <div v-if="visible" class="la-legend" :class="{ 'la-legend--collapsed': collapsed }">
      <div class="la-legend-header" @click="collapsed = !collapsed">
        <span class="la-legend-title">Public land Access</span>
        <span class="material-icons la-legend-chevron">{{ collapsed ? 'expand_less' : 'expand_more' }}</span>
      </div>

      <div v-show="!collapsed" class="la-legend-body">
        <!-- Access type filters -->
        <div class="la-legend-section">
          <div
            v-for="item in legendItems"
            :key="item.access"
            class="la-legend-row"
            @click="toggleType(item.access)"
          >
            <span class="la-legend-swatch" :style="swatchStyle(item)" :class="{ 'la-legend-swatch--off': !item.visible }"></span>
            <span class="la-legend-label" :class="{ 'la-legend-label--off': !item.visible }">{{ item.label }}</span>
          </div>
        </div>

        <!-- Special overlays -->
        <div class="la-legend-divider"></div>
        <div class="la-legend-section">
          <div class="la-legend-row" @click="toggleSeasonal">
            <span class="la-legend-swatch la-legend-swatch--pattern la-legend-swatch--seasonal" :class="{ 'la-legend-swatch--off': !seasonalVisible }"></span>
            <span class="la-legend-label" :class="{ 'la-legend-label--off': !seasonalVisible }">Seasonal areas</span>
          </div>
          <div class="la-legend-row" @click="toggleHunting">
            <span class="la-legend-swatch la-legend-swatch--pattern la-legend-swatch--hunting" :class="{ 'la-legend-swatch--off': !huntingVisible }"></span>
            <span class="la-legend-label" :class="{ 'la-legend-label--off': !huntingVisible }">Hunting areas</span>
          </div>
        </div>

        <div class="la-legend-divider"></div>

        <p class="text-muted"><small><strong>Warning:</strong> Land access data is approximate and may not be current. Always verify with the local land agency before visiting. Use at your own risk. <a target="_blank" href="https://support.hiiker.app/faq/public-land-access/">learn More</a></small></p>
      </div>
    </div>
  </transition>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed } from 'vue';
import {
  LAND_ACCESS_LEGEND,
  setAccessTypeVisible,
  setSeasonalVisible,
  setHuntingVisible,
  isSeasonalVisible,
  isHuntingVisible,
  getVisibleAccessTypes,
} from '../base_map_dialog/helpers/LandAccessOverlay';

export default defineComponent({
  name: 'LandAccessLegend',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const collapsed = ref(false);
    const seasonalVisible = ref(true);
    const huntingVisible = ref(true);

    const typeVisibility = reactive<Record<string, boolean>>({});
    LAND_ACCESS_LEGEND.forEach(item => {
      typeVisibility[item.access] = true;
    });

    const legendItems = computed(() =>
      LAND_ACCESS_LEGEND.map(item => ({
        ...item,
        visible: typeVisibility[item.access] !== false,
      }))
    );

    function swatchStyle(item: { color: string; visible: boolean }) {
      return {
        backgroundColor: item.visible ? item.color : '#666',
      };
    }

    function toggleType(access: string) {
      const next = !typeVisibility[access];
      typeVisibility[access] = next;
      setAccessTypeVisible(access, next);
    }

    function toggleSeasonal() {
      seasonalVisible.value = !seasonalVisible.value;
      setSeasonalVisible(seasonalVisible.value);
    }

    function toggleHunting() {
      huntingVisible.value = !huntingVisible.value;
      setHuntingVisible(huntingVisible.value);
    }

    return {
      collapsed,
      seasonalVisible,
      huntingVisible,
      legendItems,
      swatchStyle,
      toggleType,
      toggleSeasonal,
      toggleHunting,
    };
  },
});
</script>

<style>
/* ── Legend panel ─────────────────────────────────────────────────── */
.la-legend {
  position: absolute;
  bottom: 55px;
  right: 12px;
  z-index: 600;
  background: rgba(23, 24, 29, 0.92);
  backdrop-filter: blur(8px);
  border-radius: 10px;
  color: #fff;
  font-size: 13px;
  min-width: 180px;
  max-width: 220px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.3);
  overflow: hidden;
  user-select: none;
}

.la-legend-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  cursor: pointer;
}
.la-legend-header:hover {
  background: rgba(255,255,255,0.06);
}

.la-legend-title {
  font-weight: 600;
  font-size: 13px;
}

.la-legend-chevron {
  font-size: 20px;
  opacity: 0.7;
}

.la-legend-body {
  padding: 0 10px 10px;
}

.la-legend-section {
  padding: 2px 0;
}

.la-legend-divider {
  height: 1px;
  background: rgba(255,255,255,0.12);
  margin: 6px 0;
}

.la-legend-row {
  display: flex;
  align-items: center;
  padding: 4px 2px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s;
}
.la-legend-row:hover {
  background: rgba(255,255,255,0.08);
}

.la-legend-swatch {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  margin-right: 8px;
  flex-shrink: 0;
  transition: background-color 0.2s, opacity 0.2s;
}
.la-legend-swatch--off {
  opacity: 0.35;
}

.la-legend-swatch--seasonal {
  background: repeating-linear-gradient(
    45deg,
    #A5D6A7,
    #A5D6A7 3px,
    rgba(121,85,72,0.55) 3px,
    rgba(121,85,72,0.55) 5px
  ) !important;
}
.la-legend-swatch--hunting {
  background: repeating-linear-gradient(
    45deg,
    #E65100,
    #E65100 3px,
    rgba(230,81,0,0.3) 3px,
    rgba(230,81,0,0.3) 5px
  ) !important;
}

.la-legend-label {
  font-size: 12px;
  line-height: 1.3;
  transition: opacity 0.2s;
}
.la-legend-label--off {
  opacity: 0.45;
}

/* ── Fade transition ──────────────────────────────────────────── */
.la-legend-fade-enter-active,
.la-legend-fade-leave-active {
  transition: opacity 0.25s ease;
}
.la-legend-fade-enter-from,
.la-legend-fade-leave-to {
  opacity: 0;
}

/* ── Mobile adjustments ───────────────────────────────────────── */
@media (max-width: 600px) {
  .la-legend {
    bottom: 45px;
    right: 8px;
    min-width: 160px;
    max-width: 190px;
    font-size: 12px;
  }
}
</style>
